/**
 * sl-automation-refactor.5: AutomationLifecycleService
 *
 * Handles automation block lifecycle events and syncs with backend.
 * Detects block moves to/from trash and permanent deletions.
 *
 * Events:
 * - Block → Trash: Call trashConfig()
 * - Trash → Block (Restore): Call restoreConfig()
 * - Permanent Delete: Call deleteConfig()
 *
 * SOLID:
 * - S: Single Responsibility - Only handles lifecycle sync
 * - O: Open for extension via event handlers
 * - D: Depends on abstractions (ConfigApiService, DiscoveryService)
 */

import { Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';
import { distinctUntilChanged, pairwise, startWith } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { AutomationConfigApiService } from './automation-config-api';
import type {
  AutomationDiscoveryService,
  AutomationInfo,
} from './automation-discovery';

// =============================================================================
// Types
// =============================================================================

export interface LifecycleEvent {
  type: 'trashed' | 'restored' | 'deleted';
  blockId: string;
  docId: string;
  timestamp: number;
}

// =============================================================================
// Service
// =============================================================================

export class AutomationLifecycleService extends Service {
  private subscription: Subscription | null = null;
  private previousState: Map<string, AutomationInfo> = new Map();
  private initialized = false;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly discoveryService: AutomationDiscoveryService,
    private readonly configApiService: AutomationConfigApiService
  ) {
    super();
  }

  /**
   * sl-automation-refactor.6: Check for orphaned configs at startup.
   * Compares backend configs against known blocks and deletes orphans.
   */
  async checkOrphanedConfigs(): Promise<{ deleted: number }> {
    const workspaceId = this.workspaceService.workspace?.id;
    if (!workspaceId) {
      console.warn('[Lifecycle] No workspace ID for orphan check');
      return { deleted: 0 };
    }

    console.log('[Lifecycle] Checking for orphaned configs...');

    try {
      // Get all known automation blocks
      const knownBlockIds = new Set(
        this.discoveryService.automations$.value.map(a => a.blockId)
      );

      // Get all trashed configs from backend
      const trashedConfigs =
        await this.configApiService.listTrashed(workspaceId);

      let deletedCount = 0;

      for (const config of trashedConfigs) {
        // If block no longer exists at all (not in active or trash)
        if (!knownBlockIds.has(config.blockId)) {
          console.log(
            `[Lifecycle] Found orphaned config ${config.id} for missing block ${config.blockId}`
          );

          // Delete the orphaned config
          const deleted = await this.configApiService.deleteConfig(
            config.blockId,
            workspaceId,
            config.docId
          );

          if (deleted) {
            deletedCount++;
          }
        }
      }

      if (deletedCount > 0) {
        console.log(`[Lifecycle] Deleted ${deletedCount} orphaned configs`);
      } else {
        console.log('[Lifecycle] No orphaned configs found');
      }

      return { deleted: deletedCount };
    } catch (error) {
      console.error('[Lifecycle] Orphan check failed:', error);
      return { deleted: 0 };
    }
  }

  /**
   * Start watching for lifecycle events.
   * Call this once when the workspace is ready.
   */
  startWatching(): void {
    if (this.initialized) return;
    this.initialized = true;

    console.log('[Lifecycle] Starting automation lifecycle watcher');

    // Subscribe to automation changes
    this.subscription = this.discoveryService.automations$
      .pipe(
        startWith([] as AutomationInfo[]),
        distinctUntilChanged(
          (prev, curr) =>
            this.serializeState(prev) === this.serializeState(curr)
        ),
        pairwise()
      )
      .subscribe(([previous, current]) => {
        this.handleStateChange(previous, current);
      });
  }

  /**
   * Stop watching for lifecycle events.
   */
  stopWatching(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this.initialized = false;
    this.previousState.clear();
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  private serializeState(automations: AutomationInfo[]): string {
    // Create a stable string representation for comparison
    return automations
      .map(a => `${a.blockId}:${a.isInTrash}`)
      .sort()
      .join(',');
  }

  private handleStateChange(
    previous: AutomationInfo[],
    current: AutomationInfo[]
  ): void {
    const prevMap = new Map(previous.map(a => [a.blockId, a]));
    const currMap = new Map(current.map(a => [a.blockId, a]));

    const workspaceId = this.workspaceService.workspace?.id;
    if (!workspaceId) {
      console.warn('[Lifecycle] No workspace ID available');
      return;
    }

    // Detect changes
    for (const [blockId, prevInfo] of prevMap) {
      const currInfo = currMap.get(blockId);

      if (!currInfo) {
        // Block was permanently deleted (not in current list at all)
        void this.onBlockDeleted(blockId, prevInfo.docId, workspaceId);
      } else if (!prevInfo.isInTrash && currInfo.isInTrash) {
        // Block moved to trash
        void this.onBlockTrashed(blockId, prevInfo.docId, workspaceId);
      } else if (prevInfo.isInTrash && !currInfo.isInTrash) {
        // Block restored from trash
        void this.onBlockRestored(blockId, currInfo.docId, workspaceId);
      }
    }

    // Update previous state
    this.previousState = currMap;
  }

  private async onBlockTrashed(
    blockId: string,
    docId: string,
    workspaceId: string
  ): Promise<void> {
    console.log(`[Lifecycle] Block ${blockId} moved to trash`);

    try {
      const result = await this.configApiService.trashConfig(
        blockId,
        workspaceId,
        docId
      );

      if (result) {
        console.log(`[Lifecycle] Config ${blockId} trashed successfully`);
      } else {
        // Config might not exist in backend yet - that's OK
        console.log(
          `[Lifecycle] No config found for ${blockId} (may not exist)`
        );
      }
    } catch (error) {
      console.error(`[Lifecycle] Failed to trash config ${blockId}:`, error);
    }
  }

  private async onBlockRestored(
    blockId: string,
    docId: string,
    workspaceId: string
  ): Promise<void> {
    console.log(`[Lifecycle] Block ${blockId} restored from trash`);

    try {
      const result = await this.configApiService.restoreConfig(
        blockId,
        workspaceId,
        docId
      );

      if (result) {
        console.log(`[Lifecycle] Config ${blockId} restored successfully`);
      } else {
        console.log(
          `[Lifecycle] No config found for ${blockId} (may not exist)`
        );
      }
    } catch (error) {
      console.error(`[Lifecycle] Failed to restore config ${blockId}:`, error);
    }
  }

  private async onBlockDeleted(
    blockId: string,
    docId: string,
    workspaceId: string
  ): Promise<void> {
    console.log(`[Lifecycle] Block ${blockId} permanently deleted`);

    try {
      const result = await this.configApiService.deleteConfig(
        blockId,
        workspaceId,
        docId
      );

      if (result) {
        console.log(`[Lifecycle] Config ${blockId} deleted successfully`);
      } else {
        console.log(
          `[Lifecycle] No config found for ${blockId} (may not exist)`
        );
      }
    } catch (error) {
      console.error(`[Lifecycle] Failed to delete config ${blockId}:`, error);
    }
  }

  // ===========================================================================
  // Cleanup
  // ===========================================================================

  override dispose(): void {
    this.stopWatching();
    super.dispose();
  }
}
